import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CreditCard, ShieldCheck, Star, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import { banners, categorias } from "../mockData";
import { createReview, getCategories, getHomeSettings, getProducts, getReviews } from "../lib/api";
import Seo from "../components/Seo";
import { buildAbsoluteUrl, getSiteUrl } from "../lib/seo";

const buildCategoryFallbackCandidates = (slug, explicitFallback) => {
  const candidates = [];
  if (explicitFallback) {
    candidates.push(explicitFallback);
  }
  candidates.push(`/${slug}.jpg`);
  if (typeof window !== "undefined") {
    const firstSegment = window.location.pathname.split("/").filter(Boolean)[0];
    if (firstSegment) {
      candidates.push(`/${firstSegment}/${slug}.jpg`);
    }
  }
  return Array.from(new Set(candidates.filter(Boolean)));
};

const getDefaultCategoryImage = (slugOrTemplate) => {
  if (slugOrTemplate === "hombre") return "/hombre.jpg";
  if (slugOrTemplate === "mujer") return "/mujer.jpg";
  if (slugOrTemplate === "accesorios") return "/accesorios.jpg";
  return "/accesorios.jpg";
};

const Home = () => {
  const [bannerActual, setBannerActual] = useState(0);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [homeSettings, setHomeSettings] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [latestBannerIndex, setLatestBannerIndex] = useState(0);
  const [reviewsSummary, setReviewsSummary] = useState({
    total: 0,
    average_rating: 0,
    items: [],
  });
  const [reviewForm, setReviewForm] = useState({
    name: "",
    email: "",
    comment: "",
    rating: 5,
  });
  const [reviewSaving, setReviewSaving] = useState(false);
  const siteUrl = getSiteUrl();
  const logoUrl = buildAbsoluteUrl("/logo.png");
  const seoJsonLd = siteUrl
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Eclipse Tenis",
          url: siteUrl,
          logo: logoUrl,
          sameAs: ["https://www.instagram.com/eclipse.tenis?igsh=c3g4M2VhcHVvanZv"],
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+57 3184215616",
              contactType: "customer service",
              areaServed: "CO",
              availableLanguage: ["es"],
            },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Eclipse Tenis",
          url: siteUrl,
          potentialAction: {
            "@type": "SearchAction",
            target: `${siteUrl}/catalogo/todos?search={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        },
      ]
    : undefined;

  useEffect(() => {
    let isMounted = true;
    getCategories()
      .then((data) => {
        if (!isMounted) return;
        setDynamicCategories(Array.isArray(data) ? data : []);
        setCategoriesLoaded(true);
      })
      .catch(() => {
        if (!isMounted) return;
        setDynamicCategories([]);
        setCategoriesLoaded(true);
      });
    getHomeSettings()
      .then((data) => {
        if (isMounted) setHomeSettings(data || null);
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const loadReviews = () =>
    getReviews()
      .then((data) => {
        setReviewsSummary({
          total: Number(data?.total || 0),
          average_rating: Number(data?.average_rating || 0),
          items: Array.isArray(data?.items) ? data.items : [],
        });
      })
      .catch(() => {
        setReviewsSummary({ total: 0, average_rating: 0, items: [] });
      });

  useEffect(() => {
    loadReviews();
  }, []);

  const heroImages = homeSettings?.hero_images || {};
  const heroBannerContent = homeSettings?.hero_banners || {};
  const homeBanners = banners.slice(0, 4).map((banner, index) => {
    const key = `banner_${index + 1}`;
    const nextImage = heroImages[key];
    const nextContent = heroBannerContent[key] || {};
    return {
      ...banner,
      titulo: nextContent.title || banner.titulo,
      subtitulo: nextContent.subtitle || banner.subtitulo,
      cta: nextContent.cta || banner.cta,
      link: nextContent.link || banner.link,
      steps:
        Array.isArray(nextContent.steps) && nextContent.steps.length > 0
          ? nextContent.steps
          : banner.steps,
      imagen: nextImage || banner.imagen,
    };
  });
  const bannersData = homeBanners.length ? homeBanners : banners;
  const activeBanner = bannersData[bannerActual] || null;
  const activeIsWhatsappBanner =
    !!activeBanner &&
    typeof activeBanner.link === "string" &&
    (activeBanner.link.includes("wa.link") || activeBanner.link.includes("wa.me"));
  const categoryImages = homeSettings?.category_images || {};
  const categoryBannerOverrides = homeSettings?.category_banners || {};
  const sourceCategories =
    categoriesLoaded
      ? dynamicCategories.map((item) => ({
          nombre: item.name,
          slug: item.slug,
          descripcion: item.description || "",
          templateSlug: item.template_slug || item.slug,
          imagen: item.image_url || getDefaultCategoryImage(item.template_slug || item.slug),
        }))
      : categorias;
  const homeCategorias = sourceCategories.map((categoria) => {
    const nextImage = categoryImages[categoria.slug];
    const nextBannerContent = categoryBannerOverrides[categoria.slug] || {};
    const titleOverride = String(nextBannerContent?.title || "").trim();
    const subtitleOverride = String(nextBannerContent?.subtitle || "").trim();
    if (!nextImage) {
      return {
        ...categoria,
        nombre: titleOverride || categoria.nombre,
        descripcion: subtitleOverride || categoria.descripcion,
      };
    }
    const safeImage =
      typeof nextImage === "string" && nextImage.startsWith("http://")
        ? nextImage.replace("http://", "https://")
        : nextImage;
    return {
      ...categoria,
      nombre: titleOverride || categoria.nombre,
      descripcion: subtitleOverride || categoria.descripcion,
      imagen: typeof safeImage === "string" ? safeImage.replaceAll("\\", "/") : safeImage,
      fallbackImage: categoria.imagen,
    };
  });
  useEffect(() => {
    if (!bannersData.length) return undefined;
    const timer = setInterval(() => {
      setBannerActual((prev) => (prev + 1) % bannersData.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [bannersData.length]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    getProducts({ limit: 100 })
      .then((data) => {
        if (isMounted) {
          setProductos(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setProductos([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const latest = productos.slice(0, 10);
    if (!latest.length) return undefined;
    const timer = setInterval(() => {
      setLatestBannerIndex((prev) => (prev + 1) % latest.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [productos]);

  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    const comment = reviewForm.comment.trim();
    if (!comment) return;
    setReviewSaving(true);
    try {
      await createReview({
        name: reviewForm.name.trim() || null,
        email: reviewForm.email.trim() || null,
        rating: Number(reviewForm.rating || 5),
        comment,
      });
      setReviewForm({ name: "", email: "", comment: "", rating: 5 });
      loadReviews();
    } catch (_) {
    } finally {
      setReviewSaving(false);
    }
  };

  const siguienteBanner = () => {
    if (!bannersData.length) return;
    setBannerActual((prev) => (prev + 1) % bannersData.length);
  };

  const bannerAnterior = () => {
    if (!bannersData.length) return;
    setBannerActual((prev) => (prev - 1 + bannersData.length) % bannersData.length);
  };

  const productosRecientes = productos.slice(0, 4);
  const productosMasVendidos = productos.slice(4, 8);
  const latestProducts = productos.slice(0, 10);
  const activeLatestProduct =
    latestProducts.length > 0
      ? latestProducts[latestBannerIndex % latestProducts.length]
      : null;
  const productosOferta = productos.filter((producto) => producto.sale_price);
  const masVendidos =
    productosMasVendidos.length > 0 ? productosMasVendidos : productosRecientes;

  return (
    <div className="min-h-screen">
      <Seo
        title="Comprar tenis en Colombia"
        description="Compra tenis en Colombia, tenis a credito y zapatillas para hombre y mujer. Envio rapido, pago a cuotas y atencion por WhatsApp."
        jsonLd={seoJsonLd}
      />
      {/* Hero Banner */}
      <section className="relative h-[320px] md:h-[380px] overflow-hidden">
        {bannersData.map((banner, index) => {
          const bannerLink = String(banner.link || "");
          const bannerCta = String(banner.cta || "").trim();
          const isWhatsappBanner =
            bannerLink.includes("wa.link") || bannerLink.includes("wa.me");
          const hasSteps = Array.isArray(banner.steps) && banner.steps.length > 0;
          const hasCta = bannerCta && bannerLink;
          const contentClass = isWhatsappBanner
            ? "px-6 md:px-12 py-10 md:py-0 md:w-[60%] lg:w-[55%] text-center mx-auto flex flex-col items-center"
            : hasSteps
              ? "px-6 md:px-12 md:ml-10 max-w-xl text-left"
              : "px-4 max-w-2xl text-center";
          const titleClass = isWhatsappBanner
            ? "text-4xl md:text-6xl lg:text-6xl"
            : hasSteps
              ? "text-3xl md:text-5xl"
              : "text-4xl md:text-6xl";
          const subtitleClass = isWhatsappBanner
            ? "text-lg md:text-2xl"
            : hasSteps
              ? "text-base md:text-lg"
              : "text-lg md:text-xl";
          const overlayClass = isWhatsappBanner
            ? "bg-black/50 md:bg-gradient-to-r md:from-black/80 md:via-black/60 md:to-black/20 justify-center"
            : hasSteps
              ? "bg-black/20 md:bg-gradient-to-r md:from-black/45 md:via-black/20 md:to-transparent justify-start"
              : "bg-black/40 justify-center";
          return (
            <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === bannerActual
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
          >
              <div className="relative h-full">
                <img
                  src={banner.imagen}
                  alt={banner.titulo}
                  className="h-full w-full object-cover object-center"
                  onError={(event) => {
                    if (!banner.fallbackImage) return;
                    if (event.currentTarget.src.endsWith(banner.fallbackImage)) return;
                    event.currentTarget.src = banner.fallbackImage;
                  }}
                />
                <div
                  className={`absolute inset-0 flex items-center ${overlayClass}`}
                >
                  <div
                    className={`text-white ${contentClass}`}
                  >
                    {isWhatsappBanner && (
                      <span className="inline-flex items-center gap-2 rounded-full bg-green-500/20 px-4 py-1 text-sm font-semibold uppercase tracking-wide text-green-200 mx-auto">
                        WhatsApp directo
                      </span>
                    )}
                    <h1
                      className={`font-bold mb-4 ${titleClass}`}
                    >
                      {banner.titulo}
                    </h1>
                    <p
                      className={`mb-4 ${subtitleClass}`}
                    >
                      {banner.subtitulo}
                    </p>
                    {hasSteps && (
                      <div className="mb-6 rounded-2xl bg-black/35 p-4 text-sm md:text-base backdrop-blur">
                        <p className="text-xs uppercase tracking-widest text-white/70 mb-3">
                          Pasos rapidos
                        </p>
                        <ol className="grid gap-2 md:grid-cols-2 list-decimal list-inside text-white/90">
                          {banner.steps.map((step, stepIndex) => (
                            <li key={`${banner.id}-step-${stepIndex}`} className="leading-relaxed">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {isWhatsappBanner && (
                      <p className="text-sm md:text-base text-white/90 mb-6">
                        Resuelve dudas, novedades y pagos con Sistecredito en minutos.
                      </p>
                    )}
                    {hasCta && bannerLink.startsWith("http") ? (
                      <Button
                        asChild
                        size="lg"
                        className={
                          isWhatsappBanner
                            ? "bg-green-500 text-white hover:bg-green-600 mx-auto"
                            : "bg-white text-black hover:bg-gray-200"
                        }
                      >
                        <a href={bannerLink} target="_blank" rel="noopener noreferrer">
                          {bannerCta}
                        </a>
                      </Button>
                    ) : hasCta ? (
                      <Button size="lg" asChild className="bg-white text-black hover:bg-gray-200">
                        <Link to={bannerLink}>{bannerCta}</Link>
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Controles del banner */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
          onClick={bannerAnterior}
        >
          <ChevronLeft className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
          onClick={siguienteBanner}
        >
          <ChevronRight className="h-6 w-6" />
        </Button>

        {/* Indicadores */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 flex gap-2 rounded-full bg-black/25 px-3 py-1 backdrop-blur-sm z-20 ${
            activeIsWhatsappBanner ? "bottom-24 md:bottom-20" : "bottom-16"
          }`}
        >
          {bannersData.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === bannerActual ? "bg-white w-8" : "bg-white/50"
              }`}
              onClick={() => setBannerActual(index)}
            />
          ))}
        </div>

        <div className="absolute bottom-0 left-0 w-full bg-black/85">
          <div className="container mx-auto px-4 py-3 text-center text-xs font-semibold uppercase tracking-widest text-white md:text-sm">
            Eclipse Tenis - Tenis AAA y 1.1 en Colombia
          </div>
        </div>
      </section>

      {/* Mas recientes */}
      <section className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold">Mas Recientes</h2>
          <Link to="/catalogo/todos">
            <Button variant="outline">Ver todos</Button>
          </Link>
        </div>
        {loading && <p className="text-sm text-gray-500">Cargando productos...</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {productosRecientes.map((producto) => (
            <ProductCard key={producto.id} producto={producto} showWhatsappLabel={false} />
          ))}
        </div>
      </section>

      {/* Categorias */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Compra por Categoria</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {homeCategorias.map((categoria) => (
              <Link
                key={categoria.slug}
                to={`/catalogo/${categoria.slug}`}
                className="group relative overflow-hidden rounded-lg aspect-[4/3]"
              >
                <img
                  src={categoria.imagen}
                  alt={categoria.nombre}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(event) => {
                    const candidates = buildCategoryFallbackCandidates(
                      categoria.slug,
                      categoria.fallbackImage || categoria.imagen,
                    );
                    const step = Number(event.currentTarget.dataset.fallbackStep || "0");
                    const nextCandidate = candidates[step];
                    if (!nextCandidate) return;
                    event.currentTarget.dataset.fallbackStep = String(step + 1);
                    event.currentTarget.src = nextCandidate;
                  }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                  <div className="px-5 text-center text-white">
                    <h3 className="text-3xl font-bold">{categoria.nombre}</h3>
                    {categoria.descripcion && (
                      <p className="mt-3 text-sm md:text-base text-white/90 max-w-xs">
                        {categoria.descripcion}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {activeLatestProduct && (
        <section className="container mx-auto px-4 py-12">
          <div className="rounded-2xl border border-black/10 bg-white shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative h-72 md:h-80">
                {latestProducts.map((item, index) => (
                  <img
                    key={`latest-${item.id}`}
                    src={item.image_url}
                    alt={item.name}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                      index === latestBannerIndex % latestProducts.length
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                  />
                ))}
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                  Ultimos ingresos
                </p>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">
                  {activeLatestProduct.name}
                </h3>
                {activeLatestProduct.brand && (
                  <p className="text-sm text-gray-600 mb-2">
                    Marca: {activeLatestProduct.brand}
                  </p>
                )}
                <p className="text-3xl font-bold mb-6">
                  ${Number(activeLatestProduct.sale_price || activeLatestProduct.price).toLocaleString("es-CO")}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {latestProducts.map((item, index) => (
                    <button
                      key={`dot-${item.id}`}
                      type="button"
                      onClick={() => setLatestBannerIndex(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === latestBannerIndex % latestProducts.length
                          ? "w-8 bg-black"
                          : "w-2.5 bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <Button asChild className="w-full md:w-auto">
                  <Link to={`/producto/${activeLatestProduct.id}`}>Ver producto</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Los mas vendidos */}
      <section className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold">Los Mas Vendidos</h2>
          <Link to="/catalogo/todos">
            <Button variant="outline">Ver todos</Button>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {masVendidos.map((producto) => (
            <ProductCard key={producto.id} producto={producto} showWhatsappLabel={false} />
          ))}
        </div>
      </section>

      {/* Ofertas */}
      {productosOferta.length > 0 && (
        <section className="bg-gradient-to-r from-red-50 to-pink-50 py-16">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-red-600">Ofertas Especiales</h2>
              <Link to="/ofertas">
                <Button
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                >
                  Ver todas las ofertas
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {productosOferta.map((producto) => (
                <ProductCard key={producto.id} producto={producto} showWhatsappLabel={false} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <CardReview
            reviewForm={reviewForm}
            setReviewForm={setReviewForm}
            reviewSaving={reviewSaving}
            onSubmit={handleReviewSubmit}
          />
          <div className="rounded-2xl border border-black/10 bg-white p-6 shadow-sm">
            <h3 className="text-2xl font-bold mb-2">Reseñas de clientes</h3>
            <p className="text-sm text-gray-600 mb-4">
              {reviewsSummary.total} reseñas - promedio {reviewsSummary.average_rating.toFixed(1)} / 5
            </p>
            <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
              {reviewsSummary.items.map((review) => (
                <div key={review.id} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold">{review.name || "Cliente"}</p>
                    <div className="flex items-center gap-1 text-amber-500">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <Star
                          key={`${review.id}-star-${idx}`}
                          className={`h-4 w-4 ${
                            idx < Number(review.rating || 0)
                              ? "fill-amber-500 text-amber-500"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">{review.comment}</p>
                </div>
              ))}
              {reviewsSummary.items.length === 0 && (
                <p className="text-sm text-gray-500">
                  Aun no hay reseñas. Sé el primero en calificar la tienda.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="relative overflow-hidden py-16">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50"></div>
        <div className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-emerald-200/40 blur-3xl"></div>
        <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-amber-200/30 blur-3xl"></div>
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group rounded-3xl border border-black/5 bg-white/90 p-7 text-center shadow-[0_12px_30px_-20px_rgba(0,0,0,0.35)] backdrop-blur transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.45)]">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-700">
                ENVIO
              </span>
              <div className="mx-auto mt-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 shadow-sm">
                <Truck className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-bold">Envio Gratis</h3>
              <p className="mt-2 text-gray-600">Envio gratis desde $200.000. Envio $10.000 en compras menores.</p>
              <div className="mx-auto mt-5 h-1 w-12 rounded-full bg-emerald-500/80"></div>
            </div>
            <div className="group rounded-3xl border border-black/5 bg-white/90 p-7 text-center shadow-[0_12px_30px_-20px_rgba(0,0,0,0.35)] backdrop-blur transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.45)]">
              <span className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-sky-700">
                PAGO
              </span>
              <div className="mx-auto mt-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 shadow-sm">
                <CreditCard className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-bold">Paga a Cuotas</h3>
              <p className="mt-2 text-gray-600">Con Sistecredito via WhatsApp. Recibe un cupon del 5% en compras superiores a $200.000.</p>
              <div className="mx-auto mt-5 h-1 w-12 rounded-full bg-sky-500/80"></div>
            </div>
            <div className="group rounded-3xl border border-black/5 bg-white/90 p-7 text-center shadow-[0_12px_30px_-20px_rgba(0,0,0,0.35)] backdrop-blur transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.45)]">
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-amber-700">
                OK
              </span>
              <div className="mx-auto mt-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 shadow-sm">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-bold">Calidad Garantizada</h3>
              <p className="mt-2 text-gray-600">Las mejores marcas y productos</p>
              <div className="mx-auto mt-5 h-1 w-12 rounded-full bg-amber-500/80"></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CardReview = ({ reviewForm, setReviewForm, reviewSaving, onSubmit }) => (
  <div className="rounded-2xl border border-black/10 bg-white p-6 shadow-sm">
    <h3 className="text-2xl font-bold mb-2">Califica Eclipse Tenis</h3>
    <p className="text-sm text-gray-600 mb-4">
      Tu opinion nos ayuda a mejorar. Puntua de 1 a 5 estrellas.
    </p>
    <form className="space-y-4" onSubmit={onSubmit}>
      <input
        type="text"
        placeholder="Nombre (opcional)"
        value={reviewForm.name}
        onChange={(event) =>
          setReviewForm((prev) => ({ ...prev, name: event.target.value }))
        }
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
      />
      <input
        type="email"
        placeholder="Correo (opcional)"
        value={reviewForm.email}
        onChange={(event) =>
          setReviewForm((prev) => ({ ...prev, email: event.target.value }))
        }
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
      />
      <textarea
        placeholder="Escribe tu resena"
        value={reviewForm.comment}
        onChange={(event) =>
          setReviewForm((prev) => ({ ...prev, comment: event.target.value }))
        }
        className="w-full min-h-[130px] rounded-md border border-gray-300 px-3 py-2 text-sm"
        required
      />
      <div>
        <p className="mb-2 text-sm font-medium">Calificacion</p>
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, index) => {
            const value = index + 1;
            return (
              <button
                key={`form-star-${value}`}
                type="button"
                onClick={() => setReviewForm((prev) => ({ ...prev, rating: value }))}
                className="rounded p-1"
              >
                <Star
                  className={`h-6 w-6 ${
                    value <= Number(reviewForm.rating || 0)
                      ? "fill-amber-500 text-amber-500"
                      : "text-gray-300"
                  }`}
                />
              </button>
            );
          })}
        </div>
      </div>
      <Button type="submit" disabled={reviewSaving} className="w-full md:w-auto">
        {reviewSaving ? "Enviando..." : "Enviar resena"}
      </Button>
    </form>
  </div>
);

export default Home;
